import React, { useEffect, useCallback } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import {
  FormControlGroup,
  Input,
  FormControlLabel,
  Button,
} from "src/components";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useForm } from "react-hook-form";
import { IAddress } from "src/domains";
import * as formValidators from "src/utils/form-validator";
import postalCode from "japan-postal-code";
import { myAddressChangeEmit } from "src/utils/event";
import LocalText from "src/local";
import { RootStackScreenProps } from "src/navigation/types";

export type MyAddressNavigationParam = {
  address?: IAddress;
};

type FormValues = {
  postnumber?: string;
  prefecture?: string;
  city?: string;
  address?: string;
  address2?: string;
  address3?: string;
};

export const MyAddress: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"MyAddress">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"MyAddress">["route"]>();
  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: route.params.address || {},
  });

  useEffect(() => {
    register("postnumber", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.myAddress.postnumberValidationRequired;
        }
        if (value?.length !== 7) {
          return LocalText.screens.myAddress.postnumberValidationFormat;
        }
        return true;
      },
    });
    register("prefecture", {
      validate: (value) => {
        if (!getValues("postnumber") || getValues("postnumber")?.length !== 7) {
          return true;
        }
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.myAddress.prefectureValidationRequired;
        }
        return true;
      },
    });
    register("city", {
      validate: (value) => {
        if (!getValues("postnumber") || getValues("postnumber")?.length !== 7) {
          return true;
        }
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.myAddress.cityValidationRequired;
        }
        return true;
      },
    });
    register("address", {
      validate: (value) => {
        if (!getValues("postnumber") || getValues("postnumber")?.length !== 7) {
          return true;
        }
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.myAddress.addressValidationRequired;
        }
        return true;
      },
    });
  }, [register, getValues]);

  const postnumberChangeHandler = useCallback(
    (text: string) => {
      setValue("postnumber", text);
      if (text.length === 7) {
        postalCode.get(text, (address: any) => {
          setValue("prefecture", address.prefecture, { shouldValidate: true });
          setValue("city", `${address.city}${address.area}`, {
            shouldValidate: true,
          });
        });
      } else {
        setValue("prefecture", "", { shouldValidate: true });
        setValue("city", "", { shouldValidate: true });
        setValue("address2", "", { shouldValidate: true });
        setValue("address3", "", { shouldValidate: true });
      }
      setValue("address", "", { shouldValidate: true });
    },
    [setValue]
  );

  const onSave = async (data: FormValues) => {
    myAddressChangeEmit(data);
    navigation.goBack();
  };

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView}>
        <FormControlGroup>
          <Input
            label={LocalText.screens.myAddress.postnumber}
            placeholder={LocalText.screens.myAddress.postnumberPlaceholder}
            name={"postnumber"}
            control={control}
            error={errors.postnumber?.message}
            autoCapitalize={"none"}
            onChangeText={postnumberChangeHandler}
          />
          {getValues("prefecture") ? (
            <FormControlLabel
              text={LocalText.screens.myAddress.prefecture}
              detailStyle={styles.detail}
              detail={getValues("prefecture")}
              error={errors.prefecture?.message}
            />
          ) : null}
          {getValues("prefecture") ? (
            <FormControlLabel
              text={LocalText.screens.myAddress.city}
              detailStyle={styles.detail}
              detail={getValues("city")}
              error={errors.city?.message}
            />
          ) : null}
          {getValues("prefecture") ? (
            <Input
              label={LocalText.screens.myAddress.address}
              placeholder={LocalText.screens.myAddress.addressPlaceholder}
              name={"address"}
              control={control}
              error={errors.address?.message}
              autoCapitalize={"none"}
            />
          ) : null}
          {getValues("prefecture") ? (
            <Input
              label={LocalText.screens.myAddress.address2}
              placeholder={LocalText.screens.myAddress.address2Placeholder}
              name={"address2"}
              control={control}
              error={errors.address2?.message}
              autoCapitalize={"none"}
            />
          ) : null}
        </FormControlGroup>
      </ScrollView>
      <View style={styles.actionContainer}>
        <Button
          containerStyle={styles.chatActionContainer}
          variant={"secondary"}
          icon={"cancel"}
          iconColor={colors.chokuRed}
          title={LocalText.common.cancel}
          onPress={() => navigation.goBack()}
        />
        <Button
          containerStyle={styles.offerActionContainer}
          variant={"primary"}
          icon={"check"}
          title={LocalText.common.done}
          onPress={handleSubmit(onSave)}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    // backgroundColor: colors.white,
    backgroundColor: colors.gray1,
  },
  scrollView: {
    backgroundColor: colors.gray1,
  },
  actionContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: 16,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    backgroundColor: colors.white,
  },
  chatActionContainer: {
    flex: 1,
    marginRight: 8,
  },
  offerActionContainer: {
    flex: 1,
    marginLeft: 8,
  },
  detail: {
    color: colors.chokuBlack,
  },
});
